import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, BookmarkIcon, PlayIcon, SlidersHorizontalIcon, Trash2Icon } from 'lucide-react';
import { ReportDef, ReportFilter } from '../../data/reports';
import { FilterPreset, filterableFields } from '../../data/filterPresets';
interface RunReportDialogProps {
  open: boolean;
  report: ReportDef | null;
  presets: FilterPreset[];
  /** Prefill the dialog with these filters (e.g. when re-opening from a run). */
  initialFilters?: ReportFilter[];
  onClose: () => void;
  onRun: (filters: ReportFilter[], presetName: string | null) => void;
  savePreset: (reportId: string, name: string, filters: ReportFilter[]) => void;
  deletePreset: (id: string) => void;
}
type StagedFilters = Record<string, {
  operator: ReportFilter['operator'];
  value: string;
}>;
function toStaged(filters: ReportFilter[]): StagedFilters {
  const out: StagedFilters = {};
  filters.forEach(f => {
    out[f.field] = {
      operator: f.operator,
      value: f.value
    };
  });
  return out;
}
function toFilters(staged: StagedFilters): ReportFilter[] {
  return Object.entries(staged).filter(([, v]) => v.value.trim() !== '').map(([field, v]) => ({
    field,
    operator: v.operator,
    value: v.value.trim()
  }));
}
export function RunReportDialog({
  open,
  report,
  presets,
  initialFilters,
  onClose,
  onRun,
  savePreset,
  deletePreset
}: RunReportDialogProps) {
  const fields = useMemo(() => report ? filterableFields(report.id) : [], [report]);
  const [staged, setStaged] = useState<StagedFilters>({});
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [presetName, setPresetName] = useState('');
  useEffect(() => {
    if (open) {
      setStaged(initialFilters ? toStaged(initialFilters) : {});
      setActivePresetId(null);
      setPresetName('');
    }
  }, [open, report]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!report) return null;
  const applyPreset = (preset: FilterPreset) => {
    setStaged(toStaged(preset.filters));
    setActivePresetId(preset.id);
  };
  const setField = (field: string, patch: Partial<{
    operator: ReportFilter['operator'];
    value: string;
  }>) => {
    setStaged(prev => ({
      ...prev,
      [field]: {
        operator: patch.operator ?? prev[field]?.operator ?? 'contains',
        value: patch.value ?? prev[field]?.value ?? ''
      }
    }));
    setActivePresetId(null);
  };
  const clearAll = () => {
    setStaged({});
    setActivePresetId(null);
  };
  const handleSavePreset = () => {
    const filters = toFilters(staged);
    if (!presetName.trim() || filters.length === 0) return;
    savePreset(report.id, presetName.trim(), filters);
    setPresetName('');
  };
  const handleRun = () => {
    const filters = toFilters(staged);
    const preset = activePresetId ? presets.find(p => p.id === activePresetId) : null;
    onRun(filters, preset?.name ?? null);
  };
  const activeCount = toFilters(staged).length;
  return <AnimatePresence data-id="element-202">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 z-40 bg-navy-950/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" data-id="element-203" />
          <div className="pointer-events-none fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:items-center" data-id="element-204">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97,
          y: 10
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 10
        }} transition={{
          duration: 0.18
        }} role="dialog" aria-modal="true" aria-labelledby="run-report-title" className="card-float-lg pointer-events-auto my-auto w-full max-w-lg rounded-2xl bg-white" data-id="element-205">
              <div className="flex items-start justify-between border-b border-navy-50 px-5 py-4" data-id="element-206">
                <div data-id="element-207">
                  <h2 id="run-report-title" className="text-sm font-semibold text-navy-900" data-id="element-208">
                    Run “{report.name}”
                  </h2>
                  <p className="mt-0.5 text-xs text-navy-400" data-id="element-209">
                    Choose a saved filter preset or set filters for this run.
                  </p>
                </div>
                <button type="button" onClick={onClose} aria-label="Close dialog" className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-navy-400 transition-colors hover:bg-navy-50 hover:text-navy-900" data-id="element-210">
                  <XIcon className="h-4 w-4" data-id="element-211" />
                </button>
              </div>

              <div className="max-h-[60vh] space-y-5 overflow-y-auto px-5 py-4 thin-scroll" data-id="element-212">
                {/* Saved presets */}
                <div data-id="element-213">
                  <span className="block text-[11px] font-semibold uppercase tracking-wider text-navy-400" data-id="element-214">
                    Saved Filter Presets
                  </span>
                  {presets.length === 0 ? <p className="mt-2 text-xs text-navy-300" data-id="element-215">
                      No presets yet. Set filters below and save them for reuse.
                    </p> : <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-216">
                      {presets.map(preset => {
                  const isActive = activePresetId === preset.id;
                  return <span key={preset.id} className={`group inline-flex items-center gap-1.5 rounded-full border py-1 pl-3 pr-1.5 text-xs font-medium transition-all ${isActive ? 'border-navy-900 bg-navy-900 text-white' : 'border-navy-100 bg-white text-navy-700 hover:border-navy-300'}`} data-id="element-217">
                            <button type="button" onClick={() => applyPreset(preset)} className="focus:outline-none" data-id="element-218">
                              {preset.name}
                            </button>
                            {!preset.system && <button type="button" onClick={() => {
                      deletePreset(preset.id);
                      if (isActive) setActivePresetId(null);
                    }} aria-label={`Delete preset ${preset.name}`} className={`flex h-4 w-4 items-center justify-center rounded-full transition-colors ${isActive ? 'text-white/60 hover:bg-white/20 hover:text-white' : 'text-navy-300 hover:bg-navy-50 hover:text-rose-600'}`} data-id="element-219">
                                <Trash2Icon className="h-3 w-3" data-id="element-220" />
                              </button>}
                          </span>;
                })}
                    </div>}
                </div>

                {/* Filters */}
                <div data-id="element-221">
                  <div className="flex items-center justify-between" data-id="element-222">
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-navy-400" data-id="element-223">
                      <SlidersHorizontalIcon className="h-3 w-3" data-id="element-224" /> Filters
                    </span>
                    {activeCount > 0 && <button type="button" onClick={clearAll} className="text-xs font-medium text-navy-400 transition-colors hover:text-navy-900" data-id="element-225">
                        Clear all
                      </button>}
                  </div>
                  <div className="mt-2 space-y-2.5" data-id="element-226">
                    {fields.map(f => {
                  const current = staged[f.key];
                  return <div key={f.key} className="grid grid-cols-[minmax(0,7rem)_5.5rem_1fr] items-center gap-2" data-id="element-227">
                          <label htmlFor={`run-${f.key}`} className="truncate text-xs font-medium text-navy-700" data-id="element-228">
                            {f.label}
                          </label>
                          <select value={current?.operator ?? 'contains'} onChange={e => setField(f.key, {
                      operator: e.target.value as ReportFilter['operator']
                    })} aria-label={`${f.label} operator`} className="h-8 rounded-lg border border-navy-100 bg-white px-1.5 text-xs text-navy-900 focus:border-navy-300 focus:outline-none" data-id="element-229">
                            <option value="contains" data-id="element-230">contains</option>
                            <option value="equals" data-id="element-231">equals</option>
                          </select>
                          <input id={`run-${f.key}`} type="text" value={current?.value ?? ''} onChange={e => setField(f.key, {
                      value: e.target.value
                    })} placeholder={`Any ${f.label.toLowerCase()}`} className="h-8 w-full rounded-lg border border-navy-100 bg-white px-2.5 text-xs text-navy-900 placeholder:text-navy-300 focus:border-navy-300 focus:outline-none focus:ring-2 focus:ring-navy-900/10" data-id="element-232" />
                        </div>;
                })}
                  </div>
                </div>

                {/* Save current as preset */}
                <div className="rounded-xl bg-navy-50/70 p-3" data-id="element-233">
                  <span className="block text-[11px] font-semibold uppercase tracking-wider text-navy-400" data-id="element-234">
                    Save These Filters
                  </span>
                  <div className="mt-2 flex items-center gap-2" data-id="element-235">
                    <input type="text" value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="Preset name (e.g. Edmonton Job Type)" aria-label="New preset name" className="h-8 flex-1 rounded-lg border border-navy-100 bg-white px-2.5 text-xs text-navy-900 placeholder:text-navy-300 focus:border-navy-300 focus:outline-none focus:ring-2 focus:ring-navy-900/10" data-id="element-236" />
                    <button type="button" onClick={handleSavePreset} disabled={!presetName.trim() || activeCount === 0} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-navy-100 px-3 text-xs font-medium text-navy-700 transition-colors hover:border-navy-300 hover:text-navy-900 disabled:cursor-not-allowed disabled:border-navy-50 disabled:text-navy-300" data-id="element-237">
                      <BookmarkIcon className="h-3.5 w-3.5 text-gold-500" data-id="element-238" />
                      Save
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 border-t border-navy-50 px-5 py-4" data-id="element-239">
                <span className="text-xs text-navy-400" data-id="element-240">
                  {activeCount === 0 ? 'No filters — will run all records' : `${activeCount} filter${activeCount === 1 ? '' : 's'} applied`}
                </span>
                <div className="flex items-center gap-2" data-id="element-241">
                  <button type="button" onClick={onClose} className="h-9 rounded-full border border-navy-100 px-4 text-sm font-medium text-navy-600 transition-colors hover:border-navy-300 hover:text-navy-900" data-id="element-242">
                    Cancel
                  </button>
                  <button type="button" onClick={handleRun} className="inline-flex h-9 items-center gap-1.5 rounded-full bg-navy-900 px-4 text-sm font-medium text-white transition-colors hover:bg-navy-800" data-id="element-243">
                    <PlayIcon className="h-3.5 w-3.5" data-id="element-244" /> Run Report
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}